/**
 * The people behind one number on Reports → Mobile Usage.
 *
 * Every figure on that page is a count or an average over PEOPLE, and the first thing a
 * program manager asks of any of them is "which people?". Clicking a number opens this
 * window, which asks the API for exactly the people that number was computed over and lists
 * them the same way the three follow-up cards do — name, employee code, store — with the
 * figures that put each person there.
 *
 * THE WINDOW KNOWS NOTHING ABOUT ANY PARTICULAR NUMBER, on purpose. The API answers with
 * its own title, its own sentence under it, its own caveat and its own COLUMNS (a key, a
 * header and a format token). So a new drill-down is one entry in the service's registry
 * and one more clickable number on the page — never a new branch in here. That is also what
 * keeps the window's wording in step with the card it opened from: both come from the same
 * place.
 *
 * WHY THE LIST CAN DISAGREE WITH THE CARD BY A PERSON OR TWO, and why it says so. The card
 * was computed when the page loaded; this list is computed when the number is clicked.
 * Phones send their usage in windows that arrive late — a handset switched on after a few
 * days off delivers its old days then — so a person can join a settled day between the two
 * queries. Measured on project 170 on 26 Oct 2026: a day the card counted 2,526 people on
 * listed 2,527 a few minutes later. Printing the list's own count and saying when it was
 * read is honest; silently showing a different number is not.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';
import Modal from './Modal';
import { WorklistTable, type UsagePerson } from './MobileUsageWorklist';
import { mobileUsageAPI } from '../services/api';
import { count, days, minutes, n, people, seconds } from '../utils/usageNumbers';
import { downloadTableXlsx, type TableCell } from '../utils/tableXlsx';

/** What the page asks for: which drill-down, and the selectors that pin it to one number. */
export interface DrillRequest {
  drill: string;
  /** Already-safe values straight off the snapshot — a role group, an IST day, a screen
   *  name. The API validates every one of them again against a closed set. */
  params: Record<string, string>;
}

interface DrillColumn {
  key: string;
  header: string;
  format?: string;
}

type DrillRow = UsagePerson & Record<string, unknown>;

interface DrillPayload {
  card?: string;
  title?: string;
  subtitle?: string;
  caveat?: string | null;
  columns?: DrillColumn[];
  people?: number;
  rows?: DrillRow[];
  truncated?: boolean;
  degraded?: string[];
}

/** Rows a window shows before it caps. Six times the cards' fifty: this list is ranked and
 *  opens in a window of its own, so the reader can scroll to the row that matters. The API
 *  caps at 300, so this never hides a row the API sent. */
const WINDOW_ROW_CAP = 300;

/** Render one cell the way the page renders that unit. An unknown format prints the value
 *  as text rather than throwing: a column the API adds later must not blank the window. */
const cell = (format: string | undefined, value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  switch (format) {
    case 'minutes': return minutes(value);
    case 'days': return days(value);
    case 'seconds': return seconds(value);
    case 'count': return count(value);
    default: return String(value);
  }
};

/** The same value for the spreadsheet: numbers stay numbers so Excel can sort and sum
 *  them, and only genuine text is written as text. */
const sheetValue = (format: string | undefined, value: unknown): TableCell => {
  if (value === null || value === undefined || value === '') return null;
  if (format === 'text') return String(value);
  const num = Number(value);
  return Number.isFinite(num) ? num : String(value);
};

/**
 * THE LEAVER LABEL HAS TO SURVIVE THE DOWNLOAD. Showing people who have left is only
 * defensible because the table marks them; a spreadsheet that dropped the mark would be a
 * list of names with no way to tell. It is a column of its own, and it keeps isActive's
 * THREE states — "not known" is not the same as "still here", and saying the wrong one of
 * those about a person is the mistake that costs somebody their job.
 */
const stillHere = (isActive: boolean | null | undefined): string => {
  if (isActive === false) return 'No longer with the company';
  if (isActive === true) return 'Yes';
  return 'Not known';
};

const personCells = (p: DrillRow): TableCell[] => [
  p.name?.trim() || `App user ${p.userId}`,
  p.empCode?.trim() || null,
  p.store?.trim() || null,
  p.city?.trim() || null,
  stillHere(p.isActive),
];

export function MobileUsagePeopleModal({
  projectId, days: dayCount, request, onClose,
}: {
  projectId: number | null;
  days: number;
  request: DrillRequest | null;
  onClose: () => void;
}) {
  const [data, setData] = useState<DrillPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!request || !projectId) return undefined;
    let live = true;
    setLoading(true);
    setError(null);
    setData(null);
    mobileUsageAPI
      .getPeople({ projectId, days: dayCount, drill: request.drill, params: request.params })
      .then((res) => { if (live) setData(res.data?.data ?? null); })
      .catch((err: any) => {
        if (!live) return;
        // The API answers 400 for a selector it does not accept and 404 for a list that does
        // not exist; both carry a sentence worth showing. Anything else is ours to apologise
        // for in plain words.
        setError(err?.response?.data?.message ?? 'Could not open that list. Try again shortly.');
      })
      .finally(() => { if (live) setLoading(false); });
    // Cancelled by the time it answers: a second click must not be overwritten by the first.
    return () => { live = false; };
  }, [request, projectId, dayCount]);

  const rows = Array.isArray(data?.rows) ? (data?.rows as DrillRow[]) : [];
  const columns = Array.isArray(data?.columns) ? (data?.columns as DrillColumn[]) : [];
  const namesMissing = Array.isArray(data?.degraded) && data!.degraded!.includes('identity');

  const tableColumns = useMemo(
    () => columns.map((c) => ({ header: c.header, cell: (p: DrillRow) => cell(c.format, p[c.key]) })),
    [columns],
  );

  const download = useCallback(async () => {
    if (rows.length === 0) return;
    await downloadTableXlsx(data?.title || 'People', {
      name: 'People',
      headers: ['Person', 'Employee code', 'Store', 'City', 'Still with the company', ...columns.map((c) => c.header)],
      rows: rows.map((p) => [...personCells(p), ...columns.map((c) => sheetValue(c.format, p[c.key]))]),
    });
  }, [rows, columns, data?.title]);

  return (
    <Modal
      isOpen={Boolean(request)}
      onClose={onClose}
      size="2xl"
      title={data?.title || (loading ? 'Opening the list…' : 'The people behind this number')}
      footer={
        rows.length > 0 ? (
          <button
            type="button"
            onClick={() => { void download(); }}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Download this list
          </button>
        ) : undefined
      }
    >
      {loading && (
        <div className="flex items-center gap-2 px-1 py-8 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Reading this list from the usage data…
        </div>
      )}

      {!loading && error && (
        <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {data.card && <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">{data.card}</p>}
          {data.subtitle && <p className="mb-3 text-sm text-gray-600">{data.subtitle}</p>}
          {data.caveat && (
            <p className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {data.caveat}
            </p>
          )}
          {namesMissing && (
            <p className="mb-3 text-sm text-gray-600">
              The names could not be looked up this time, so these are the app&apos;s own ids. The people and the
              numbers are right.
            </p>
          )}
          <p className="mb-3 text-sm text-gray-600">
            {people(data.people)} — counted as this window opened, so it can differ by a person or two from the
            number on the card if a phone has reported since.
          </p>
          <WorklistTable<DrillRow>
            people={rows}
            total={n(data.people)}
            rowCap={WINDOW_ROW_CAP}
            countNote="the rest are further down the same order."
            emptyMessage="Nobody is behind this number."
            columns={tableColumns}
          />
        </>
      )}
    </Modal>
  );
}

export default MobileUsagePeopleModal;
